import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import DeleteIcon from "@mui/icons-material/Delete";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { useTheme, alpha } from "@mui/material/styles";
import { isEmpty } from "lodash";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { openAppConfirm } from "../../components/AppDialogs";
import MDBox from "../../components/MDBox";
import MDButton from "../../components/MDButton";
import MDTypography from "../../components/MDTypography";
import Footer from "../../examples/Footer";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import {
  AddSubTitlesCall,
  AddTitlesCall,
  DeleteSubTitlesCall,
  DeleteTitlesCall,
  EditSubTitlesCall,
  EditTitlesCall,
  GetTitlesCall,
  SwapTitlesCall,
} from "../../redux/api/title.api";
import { getQuizLinkedIdsCall } from "../../redux/api/quizSystem.api";
import { store } from "../../redux/store";
import Chip from "@mui/material/Chip";
import ProfSelector from "components/ProfSelector";
import EditableTitle from "components/EditableTitle";
import PresentationCard from "./PresentationCard";
import { invalidatePresentationPreview } from "./presentationPreview";

const PRESENTATION_TYPE = "presentation";

/** Internal organizational name only — never rendered on the canvas. */
function nextUntitledPresentationName(categories, t) {
  const base = t ? t("pages.presentationTitles.untitledPresentation") : "Untitled Presentation";
  const names = new Set();
  (categories || []).forEach((cat) => {
    (cat.subtitles || []).forEach((sub) => {
      const locs = Array.isArray(sub.titleLocalizations) ? sub.titleLocalizations : [];
      const titleLoc = locs.find((e) => e.field === "title");
      const value = String(titleLoc?.value || "").trim().toLowerCase();
      if (value) names.add(value);
    });
  });
  if (!names.has(base.toLowerCase())) return base;
  let n = 2;
  while (names.has(`${base} ${n}`.toLowerCase())) n += 1;
  return `${base} ${n}`;
}

function PresentationTitles() {
  const { t } = useTranslation();
  const theme = useTheme();
  const authId = useSelector((state) => state?.auth?.id);
  const role = useSelector((state) => state?.auth?.role);
  const [selectedProfId, setSelectedProfId] = useState("");
  // Presentations use a dedicated type so they don't mix with library titles.
  const type = PRESENTATION_TYPE;

  // Shared Redux `title.titles` also holds histology/library lists. Ignore
  // anything that was not fetched as presentations — otherwise those cards
  // flash for a second before GetTitlesCall("presentation") replaces them.
  const titlesType = useSelector((state) => state?.title?.titlesType ?? null);
  const initialItems = useSelector((state) => {
    const st = state?.title;
    if (!st || st.titlesType !== PRESENTATION_TYPE) return [];
    return Array.isArray(st.titles) ? st.titles : [];
  });
  const redux = useSelector((state) => state);

  const [fileRefs, setFileRefs] = useState({}); // store refs by subtitle ID

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [message, setMessage] = useState("");
  const [lang, setLanguage] = useState("en");
  const [linkedTileIds, setLinkedTileIds] = useState([]);

  const openSuccessSB = () => setSuccessSB(true);
  const openErrorSB = () => setErrorSB(true);

  const navigate = useNavigate();
  const isViewingOtherProf = (role === "admin" || role === "university_admin") && !!selectedProfId;

  useEffect(() => {
    let cancelled = false;
    const fetchTitles = async () => {
      if (!type) return;
      setLoading(true);
      try {
        const auth = store.getState()?.auth || {};
        const extra =
          (role === "admin" || role === "university_admin") && selectedProfId
            ? { prof_id: selectedProfId }
            : null;
        console.log("[WebPresentations] fetch start", {
          lang,
          type,
          extra,
          auth: {
            id: auth?.id ?? null,
            email: auth?.email ?? null,
            role: auth?.role ?? null,
            university_id:
              auth?.university_id ?? auth?.universityInfo?.id ?? null,
          },
        });
        const result = await GetTitlesCall(lang, type, undefined, extra);
        const list = Array.isArray(result)
          ? result
          : Array.isArray(result?.data)
            ? result.data
            : Array.isArray(store.getState()?.title?.titles) &&
                store.getState()?.title?.titlesType === PRESENTATION_TYPE
              ? store.getState().title.titles
              : [];
        const published = [];
        (list || []).forEach((root) => {
          (root?.subtitles || []).forEach((sub) => {
            if (
              sub?.is_published === true ||
              sub?.is_published === 1 ||
              sub?.is_published === "1"
            ) {
              published.push({
                id: sub.id,
                title:
                  sub?.titleLocalizations?.find?.((e) => e.field === "title")
                    ?.value || sub?.id,
                ownerType: root?.type,
                is_published: sub?.is_published,
              });
            }
          });
        });
        console.log("[WebPresentations] fetch result", {
          roots: list.length,
          publishedCount: published.length,
          published: published.slice(0, 12),
          rootsPreview: (list || []).slice(0, 5).map((r) => ({
            id: r?.id,
            type: r?.type,
            library_category: r?.library_category,
            subtitles: Array.isArray(r?.subtitles) ? r.subtitles.length : 0,
            publishedSubs: Array.isArray(r?.subtitles)
              ? r.subtitles.filter(
                  (s) =>
                    s?.is_published === true ||
                    s?.is_published === 1 ||
                    s?.is_published === "1"
                ).length
              : 0,
          })),
        });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchTitles();
    return () => {
      cancelled = true;
    };
  }, [type, lang, role, selectedProfId]);

  useEffect(() => {
    if (titlesType === PRESENTATION_TYPE) {
      setItems(initialItems);
      console.log("[WebPresentations] redux → items", {
        titlesType,
        itemCount: Array.isArray(initialItems) ? initialItems.length : 0,
      });
    } else {
      setItems([]);
    }
  }, [initialItems, titlesType]);

  useEffect(() => {
    const r = store.getState().auth?.role;
    if (r === "prof" || r === "admin" || r === "university_admin") {
      const params = (r === "admin" || r === "university_admin") && selectedProfId ? { prof_id: selectedProfId } : {};
      getQuizLinkedIdsCall(params)
        .then((res) => {
          const data = res?.data;
          if (data?.tile_ids) setLinkedTileIds(data.tile_ids);
        })
        .catch(() => setLinkedTileIds([]));
    }
  }, [selectedProfId]);

  const handleAddTitle = () => {
    AddTitlesCall({
      title: `New Title ${items?.length + 1}`,
      body: `Description ${items?.length + 1}`,
      lang,
      type,
    });
  };

  const handleAddSubtitle = async (itemId) => {
    // Internal organizational title only — never placed on the canvas.
    // The maker auto-creates a blank first page when none exist.
    const internalTitle = nextUntitledPresentationName(items, t);
    const res = await AddSubTitlesCall({
      title: internalTitle,
      title_id: itemId,
      lang,
      type,
    });
    const newId = res?.id;
    if (!newId || res?.status === 400) {
      setMessage(res?.message || "Could not create presentation.");
      setTimeout(() => openErrorSB(), 200);
      return;
    }
    if (role === "admin") {
      navigate(`/presentationMakerPage/${newId}/${lang}`);
    } else {
      navigate(`/presentationMakerPage/${newId}`);
    }
  };

  const handleEdit = (id, newValue, editType) => {
    const body = {
      id,
      lang,
      type,
      [editType === "MainTitle" || editType === "SubTitle" ? "title" : "body"]: newValue,
    };

    setItems((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.map((item) => {
        if (editType === "MainTitle" && item.id === id) {
          const locs = Array.isArray(item.titleLocalizations) ? [...item.titleLocalizations] : [];
          const titleLoc = locs.find((e) => e.field === "title");
          if (titleLoc) {
            return { ...item, titleLocalizations: locs.map((e) => (e.field === "title" ? { ...e, value: newValue } : e)) };
          }
          return { ...item, titleLocalizations: [...locs, { field: "title", value: newValue }] };
        }
        if (editType === "SubTitle" && item.subtitles) {
          return {
            ...item,
            subtitles: item.subtitles.map((sub) => {
              if (sub.id !== id) return sub;
              const locs = Array.isArray(sub.titleLocalizations) ? [...sub.titleLocalizations] : [];
              const newLocs = locs.some((e) => e.field === "title")
                ? locs.map((e) => (e.field === "title" ? { ...e, value: newValue } : e))
                : [...locs, { field: "title", value: newValue }];
              return { ...sub, titleLocalizations: newLocs };
            }),
          };
        }
        return item;
      });
    });

    if (editType === "MainTitle" || editType === "MainBody") {
      EditTitlesCall(body);
    } else {
      EditSubTitlesCall(body);
    }
  };

  const handleDeleteSubTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = await openAppConfirm({
      title: t("pages.presentationTitles.deleteSubtitleConfirm"),
      message: t("pages.presentationTitles.deleteSubtitleConfirm"),
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = DeleteSubTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage("The subtitle deleted successfully!");
        setTimeout(() => {
          openSuccessSB();
        }, 200);
      }
    }
  };
  const handleDeleteTitle = async (id) => {
    const body = {
      id,
      lang,
      type,
    };
    const confirmation = await openAppConfirm({
      title: "Delete title?",
      message:
        "Are you sure you want to delete this title and all its subtitles? All your pins, texts and slides inside this subtitle will be deleted permanently.",
      confirmText: "Delete",
      confirmColor: "error",
    });
    if (confirmation) {
      const res = await DeleteTitlesCall(body);
      if (res?.status === 400) {
        setMessage(res?.message);
        setTimeout(() => {
          openErrorSB();
        }, 200);
      } else {
        setMessage(t("pages.presentationTitles.titleDeletedSuccess"));
        setTimeout(() => {
          openSuccessSB();
        }, 200);
      }
    }
  };

  const renderItem = (item, index) => {
    const filteredTitle = item?.titleLocalizations?.filter(
      (e) => e.field == "title"
    );

    const title = filteredTitle?.[0]?.value || t("pages.presentationTitles.clickToEditTitle");
    const cardBg = theme.palette.background?.card ?? theme.palette.background?.paper ?? theme.palette.background?.default;
    const isDark = theme.palette.mode === "dark";
    const softBorder = isDark ? alpha(theme.palette.divider, 0.5) : alpha(theme.palette.primary.main, 0.08);

    return (
      <MDBox
        sx={{
          width: "100%",
          mb: 3,
          p: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          bgcolor: cardBg,
          borderRadius: 3,
          border: `1px solid ${softBorder}`,
          boxShadow: isDark ? "none" : "0 2px 8px rgba(0,0,0,0.04)",
          transition: "box-shadow 0.25s ease, border-color 0.2s ease",
          "&:hover": { boxShadow: isDark ? "0 0 0 1px " + alpha(theme.palette.primary.main, 0.2) : "0 4px 20px rgba(0,0,0,0.06)" },
        }}
      >
        {!isViewingOtherProf && (
          <MDBox display="flex" gap={0.5} sx={{ width: "100%", justifyContent: "flex-start" }}>
            {index !== 0 && (
              <IconButton
                size="small"
                onClick={() => {
                  const body = { current_id: item?.id, goal_id: items?.[index - 1]?.id, lang, type };
                  SwapTitlesCall(body);
                }}
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" },
                }}
              >
                <ArrowDropUpIcon fontSize="small" />
              </IconButton>
            )}
            {items?.length !== index + 1 && (
              <IconButton
                size="small"
                onClick={() => {
                  const body = { current_id: item?.id, goal_id: items?.[index + 1]?.id, lang, type };
                  SwapTitlesCall(body);
                }}
                sx={{
                  color: "text.secondary",
                  "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.08), color: "primary.main" },
                }}
              >
                <ArrowDropDownIcon fontSize="small" />
              </IconButton>
            )}
          </MDBox>
        )}

        <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ width: "100%" }}>
          {isViewingOtherProf ? (
            <MDTypography variant="h5" fontWeight="700" sx={{ letterSpacing: "-0.02em" }}>{title}</MDTypography>
          ) : (
            <EditableTitle
              title={title}
              titleFont={"title"}
              onEdit={(newTitle) => handleEdit(item.id, newTitle, "MainTitle")}
            />
          )}
          {linkedTileIds.includes(item.id) && (
            <Chip size="small" label={t("pages.presentationTitles.quiz")} color="info" onClick={() => navigate("/quiz/titles")} sx={{ cursor: "pointer", fontWeight: 600 }} />
          )}
        </MDBox>

        {!isViewingOtherProf && (
          <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} sx={{ width: "100%", mt: 2.5, mb: 1 }}>
            <MDButton
              color="primary"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9375rem",
                borderRadius: 3,
                px: 2.5,
                py: 1.25,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              }}
              onClick={() => handleAddSubtitle(item.id)}
            >
              {t("pages.presentationTitles.newPresentation")}
            </MDButton>
            <MDButton
              color="error"
              variant="outlined"
              startIcon={<DeleteIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 3,
                px: 2,
                py: 1.25,
                borderWidth: 1.5,
                "&:hover": { borderWidth: 1.5, bgcolor: alpha(theme.palette.error.main, 0.08) },
              }}
              onClick={() => handleDeleteTitle(item.id)}
            >
              {t("common.delete")}
            </MDButton>
          </MDBox>
        )}

        {item?.subtitles?.length > 0 && (
          <MDBox sx={{ mt: 3, width: "100%" }}>
            <Grid container spacing={2.5}>
              {item.subtitles.map((subtitle) => {
                const filteredTitle = subtitle?.titleLocalizations?.filter(
                  (e) => e.field == "title"
                );
                const presentationTitle =
                  filteredTitle?.[0]?.value ||
                  t("pages.presentationTitles.untitledPresentation");

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={subtitle.id}>
                    <PresentationCard
                      subtitle={subtitle}
                      title={presentationTitle}
                      isViewingOtherProf={isViewingOtherProf}
                      onOpen={() => {
                        if (role === "admin") {
                          navigate(`/presentationMakerPage/${subtitle.id}/${lang}`);
                        } else {
                          navigate(`/presentationMakerPage/${subtitle.id}`);
                        }
                      }}
                      onRename={(newTitle) =>
                        handleEdit(subtitle.id, newTitle, "SubTitle")
                      }
                      onDelete={() => {
                        invalidatePresentationPreview(subtitle.id);
                        handleDeleteSubTitle(subtitle.id);
                      }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </MDBox>
        )}
      </MDBox>
    );
  };

  const cardBg = theme.palette.background?.card ?? theme.palette.background?.paper ?? theme.palette.background?.default;
  const isDark = theme.palette.mode === "dark";
  const softBorder = isDark ? alpha(theme.palette.divider, 0.5) : alpha(theme.palette.primary.main, 0.12);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        sx={{
          width: "100%",
          maxWidth: "100%",
          px: 0,
          pt: 1.5,
          pb: 0,
          minHeight: "auto",
          bgcolor: "transparent",
        }}
      >
        <MDBox
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: 3,
            bgcolor: cardBg,
            border: `1px solid ${softBorder}`,
            boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <ProfSelector value={selectedProfId} onChange={setSelectedProfId} />
          {!isViewingOtherProf && (
            <MDButton
              color="primary"
              variant="contained"
              startIcon={<AddRoundedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.9375rem",
                borderRadius: 3,
                px: 2.5,
                py: 1.25,
                boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
                },
              }}
              onClick={() => handleAddTitle()}
            >
              {t("pages.presentationTitles.newCategory")}
            </MDButton>
          )}
        </MDBox>
        <MDBox sx={{ width: "100%", maxWidth: "100%" }}>
          <Grid container spacing={3} sx={{ width: "100%", maxWidth: "100%" }}>
            {loading && isEmpty(items) && (
              <Grid item xs={12} sx={{ width: "100%", maxWidth: "100%" }}>
                <MDBox
                  sx={{
                    width: "100%",
                    py: 8,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CircularProgress size={32} />
                </MDBox>
              </Grid>
            )}
            {!loading && isEmpty(items) && (
              <Grid item xs={12} sx={{ width: "100%", maxWidth: "100%" }}>
                <MDBox
                  sx={{
                    width: "100%",
                    py: { xs: 5, sm: 7 },
                    px: { xs: 2, sm: 4 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    bgcolor: cardBg,
                    borderRadius: 3,
                    border: `1px solid ${softBorder}`,
                    boxShadow: isDark ? "none" : "0 2px 12px rgba(0,0,0,0.04)",
                    maxWidth: 720,
                    mx: "auto",
                  }}
                >
                  <SlideshowOutlinedIcon
                    style={{
                      fontSize: 20,

                    }}
                    sx={{
                      fontSize: 56,
                      color: "primary.main",
                      opacity: 0.85,
                      mb: 2,
                    }}
                  />
                  <MDTypography variant="h5" fontWeight={700} sx={{ mb: 1.5, letterSpacing: "-0.02em" }}>
                    {isViewingOtherProf
                      ? t("pages.presentationTitles.emptyStateTitleViewingProf")
                      : t("pages.presentationTitles.emptyStateTitle")}
                  </MDTypography>
                  <MDTypography
                    variant="body1"
                    color="text.secondary"
                    sx={{ maxWidth: 520, lineHeight: 1.65, mb: isViewingOtherProf ? 0 : 3 }}
                  >
                    {isViewingOtherProf
                      ? t("pages.presentationTitles.emptyStateDescriptionViewingProf")
                      : t("pages.presentationTitles.emptyStateDescription")}
                  </MDTypography>
                  {!isViewingOtherProf && (
                    <MDButton
                      color="primary"
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        borderRadius: 3,
                        px: 2.5,
                        py: 1.25,
                        boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
                      }}
                      onClick={() => handleAddTitle()}
                    >
                      {t("pages.presentationTitles.emptyStateCta")}
                    </MDButton>
                  )}
                </MDBox>
              </Grid>
            )}
            {!isEmpty(items) &&
              items.map((item, index) => (
                <Grid item xs={12} key={item.id} sx={{ width: "100%", maxWidth: "100%" }}>
                  {renderItem(item, index)}
                </Grid>
              ))}
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PresentationTitles;
